import { Button, Checkbox, DotsHorizontalIcon, Dropdown, DropdownMenu, EmptyState } from '@bp/ui-components';
import classNames from 'classnames';
import { BpFile, useFiles } from 'components/Files/hooks/useFiles';
import { FileRenameModal } from 'components/Files/modals/FileRenameModal';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { naturalStringSort } from 'utils/stringHelper';
import { FileUpload } from '../FileUpload/FileUpload';
import styles from './FileList.module.scss';
import { FileListEntry } from './FileListEntry';

type FileListProps = {
  actions: ('download' | 'delete')[];
  canUpload?: boolean;
  downloadOnly?: boolean;
  files: BpFile[];
  padding?: 'none' | 'default';
  showHeader?: boolean;
  showSelect?: boolean;
  onFilesDelete?: (fileUuids: string[]) => void;
  onFileRename?: (file: BpFile) => void;
  onFileUpload?: (file: BpFile) => void;
  onSelectionChange?: (uuids: string[]) => void;
  className?: string | undefined;
};

export const FileList = ({
  actions,
  canUpload,
  downloadOnly,
  files,
  padding = 'default',
  showHeader,
  showSelect,
  onFilesDelete,
  onFileRename,
  onFileUpload,
  onSelectionChange,
  className,
}: FileListProps) => {
  const { t } = useTranslation();

  const {
    loadingUuid,
    ConfirmationDialog,
    handleCopyLink,
    handleDelete,
    handleDownload,
    handleDownloads,
    handleDeleteFiles,
  } = useFiles();

  const [selectedUuids, setSelectedUuids] = useState<string[]>([]);
  const [renameFile, setRenameFile] = useState<BpFile | null>(null);

  async function onDelete(file: BpFile) {
    await handleDelete(file);
    onFilesDelete && onFilesDelete([file.uuid]);
  }

  function handleRename(file: BpFile) {
    setRenameFile(file);
  }

  function handleSelect(uuid: string, selected: boolean) {
    const newSelectedUuids = selected ? [...selectedUuids, uuid] : selectedUuids.filter((id) => id !== uuid);
    setSelectedUuids(newSelectedUuids);
    onSelectionChange && onSelectionChange(newSelectedUuids);
  }

  function handleSelectAll(uuids: string[]) {
    const allSelected = uuids.every((u) => selectedUuids.includes(u));
    const newSelectedUuids = allSelected
      ? selectedUuids.filter((id) => !uuids.includes(id))
      : Array.from(new Set([...selectedUuids, ...uuids]));
    setSelectedUuids(newSelectedUuids);
    onSelectionChange && onSelectionChange(newSelectedUuids);
  }

  return (
    <div className={classNames(styles['file-list'], { [styles.padding]: padding === 'default' }, className)}>
      {!canUpload && files.length === 0 ? (
        <EmptyState size='small' title='' subtitle={t('files.noFiles')} hideIcon padding='l' />
      ) : (
        <>
          {showHeader && files.length > 0 && (
            <div className={styles.header}>
              {showSelect ? (
                <Checkbox
                  name='select-all'
                  label={t('files.title', { count: 2 })}
                  checked={files.length > 0 && files.every((file) => selectedUuids.includes(file.uuid))}
                  onChange={() => handleSelectAll(files.map((f) => f.uuid))}
                />
              ) : (
                t('files.title', { count: 2 })
              )}

              {actions && actions.length > 0 && (
                <div className={styles.actions}>
                  <Dropdown
                    usePortal={false}
                    trigger={<Button hierarchy='ghost' icon={<DotsHorizontalIcon className='small' />} />}
                  >
                    <DropdownMenu
                      data={[
                        ...(showSelect && actions.includes('download')
                          ? [
                              {
                                label: t('files.downloadSelected'),
                                onClick: () =>
                                  handleDownloads(
                                    files.filter((f) => selectedUuids.includes(f.uuid)),
                                    t('files.title', { count: 2 }),
                                  ),
                                disabled: selectedUuids.length === 0,
                              },
                            ]
                          : []),
                        ...(actions.includes('download')
                          ? [
                              {
                                label: t('files.downloadAll'),
                                onClick: () => handleDownloads(files, t('files.title', { count: 2 })),
                              },
                            ]
                          : []),
                        ...(actions.length > 1 && actions.includes('delete') ? [{ type: 'ruler' as const }] : []),
                        ...(actions.includes('delete')
                          ? [
                              {
                                label: t('files.deleteSelected'),
                                onClick: async () => {
                                  const selectedFiles = files.filter((f) => selectedUuids.includes(f.uuid));
                                  await handleDeleteFiles(selectedFiles);
                                  onFilesDelete && onFilesDelete(selectedFiles.map((f) => f.uuid));
                                  setSelectedUuids([]);
                                  onSelectionChange && onSelectionChange([]);
                                },
                                disabled: selectedUuids.length === 0,
                                color: 'error' as const,
                              },
                              {
                                label: t('files.deleteAll'),
                                onClick: async () => {
                                  await handleDeleteFiles(files);
                                  onFilesDelete && onFilesDelete(files.map((f) => f.uuid));
                                  setSelectedUuids([]);
                                  onSelectionChange && onSelectionChange([]);
                                },
                                color: 'error' as const,
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Dropdown>
                </div>
              )}
            </div>
          )}

          <div className={styles.entries}>
            {files
              .sort((a, b) => naturalStringSort(a.filename, b.filename))
              .map((file) => (
                <FileListEntry
                  key={file.uuid}
                  {...file}
                  loading={loadingUuid === file.uuid}
                  downloadOnly={downloadOnly}
                  selected={selectedUuids.includes(file.uuid)}
                  showSelect={showSelect}
                  onCopyLink={() => handleCopyLink(file)}
                  onDownload={() => handleDownload(file)}
                  onDelete={() => onDelete(file)}
                  onRename={() => handleRename(file)}
                  onSelect={(uuid, selected) => handleSelect(uuid, selected)}
                  className={styles.file}
                />
              ))}
          </div>

          {canUpload && (
            <div className={files.length > 0 ? 'mt-4' : undefined}>
              <FileUpload onFileUpload={(file) => onFileUpload && onFileUpload(file)} />
            </div>
          )}

          {renameFile && (
            <FileRenameModal
              isOpen={!!renameFile}
              file={renameFile}
              onRename={(name) => onFileRename && onFileRename({ ...renameFile, filename: name })}
              onClose={() => setRenameFile(null)}
            />
          )}

          <ConfirmationDialog />
        </>
      )}
    </div>
  );
};
